import React, { useState } from 'react';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import { useTranslate } from '@im/react-localization';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DatePickerProvider } from 'src/provider/DatePickerProvider';
import { DeatilsResponse, useUpdateCliamIt } from '../../hooks/deal-details';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import useSnackBar from 'src/hooks/useSnackBar';
import { LoadingButton } from '@mui/lab';
import { useDateTimeFormatter } from 'src/hooks/dateTimeFormatter';

interface EditClaimItDialogProps {
  claimName?: string;
  claimOnDate?: string;
  details?: DeatilsResponse;
  field: null | 'claimName' | 'claimOnDate';
  handleClose: () => void;
}

export default function EditClaimItDialog(
  props: Readonly<EditClaimItDialogProps>
) {
  const translate = useTranslate();
  const { showSnackBar } = useSnackBar();
  const [searchParams] = useSearchParams();
  const importreferenceid = searchParams.get('importreferenceid');
  const dealid = searchParams.get('dealid');
  const details = props.details;
  const formatDateTime = useDateTimeFormatter();
  const updateCliamIt = useUpdateCliamIt();
  const [formData, setFormData] = useState<{
    claimName: string | null;
    claimOnDate: string | null;
  }>({
    claimName: props.claimName ?? null,
    claimOnDate: props.claimOnDate ?? null,
  });
  const [formError, setFormError] = useState<{
    claimName: string;
  }>({
    claimName: '',
  });

  const getMinClaimOnDate = () => {
    const expirationDate = details?.expiration_date
      ? dayjs(formatDateTime(details.expiration_date), 'DD/MM/YYYY').add(
          1,
          'day'
        )
      : null;

    const claimMin = props.claimOnDate ? dayjs(props.claimOnDate) : null;

    if (expirationDate && claimMin) {
      return expirationDate.isAfter(claimMin)
        ? expirationDate.format('YYYY-MM-DD')
        : claimMin.format('YYYY-MM-DD');
    }

    if (expirationDate) return expirationDate.format('YYYY-MM-DD');
    if (claimMin) return claimMin.format('YYYY-MM-DD');

    return undefined;
  };
  const onChange = (field: string, value: string | null) => {
    if (field === 'claimName') {
      setFormError({
        claimName: '',
      });
    }
    setFormData({ ...formData, [field]: value });
  };

  const onUpdate = () => {
    updateCliamIt
      .mutateAsync({
        claimname: formData.claimName,
        claimondate: formData.claimOnDate
          ? dayjs(formData.claimOnDate).format('YYYY-MM-DD')
          : null,
        dealid: Number(dealid),
        importreferenceid: Number(importreferenceid),
      })
      .then(() => {
        showSnackBar({
          autoHide: 5000,
          message: translate('Common.ActionSuccess'),
          severity: 'success',
        });
        props.handleClose();
      })
      .catch(() => {
        showSnackBar({
          autoHide: 5000,
          message: translate('Common.ActionError'),
          severity: 'error',
        });
      });
  };
  return (
    <Dialog
      data-testid="gds-vp-claimit-Dialog"
      maxWidth="sm"
      onClose={updateCliamIt.isLoading ? undefined : props.handleClose}
      open={true}
      sx={{ '.MuiDialog-paper': { gap: 2, minWidth: 500 } }}
    >
      <DialogTitle>
        {translate(
          props.field === 'claimName'
            ? 'VendorDeals.ClaimUpdateClaimNameTitle'
            : 'VendorDeals.ClaimUpdateClaimOnDateTitle'
        )}
        <IconButton
          disabled={updateCliamIt.isLoading}
          onClick={props.handleClose}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ overflow: 'hidden' }}>
        <Stack gap={3}>
          {props.field === 'claimName' && (
            <TextField
              error={formError.claimName !== ''}
              helperText={formError.claimName}
              inputProps={{ 'data-testid': `gds-inut-claim-name` }}
              label={translate('VendorProgram.ProgramDetailClaimNameName')}
              name="claimName"
              onChange={(e) => onChange('claimName', e.target.value)}
              value={formData.claimName}
            />
          )}
          {props.field === 'claimOnDate' && (
            <DatePickerProvider>
              <DatePicker
                disablePast
                inputFormat="DD/MM/YYYY"
                minDate={getMinClaimOnDate()}
                onChange={(value: string | null) =>
                  onChange('claimOnDate', value)
                }
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    data-testid={`gds-inut-claim-on-date`}
                    fullWidth
                    label={translate('VendorDeals.ClaimItClaimOnDate')}
                    name={'claimOnDate'}
                    size="small"
                  />
                )}
                value={formData.claimOnDate}
              />
            </DatePickerProvider>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="gds-vp-claimit-cancel-button"
          disabled={updateCliamIt.isLoading}
          onClick={props.handleClose}
          variant="newOutlined"
        >
          {translate('Common.Cancel')}
        </Button>
        <LoadingButton
          data-testid="gds-vp-claimit-update-button"
          loading={updateCliamIt.isLoading}
          onClick={onUpdate}
          variant="newContained"
        >
          {translate('Common.Update')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
